import React, { useState, useEffect, useCallback, useMemo, createContext, useContext, useRef, useLayoutEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Fade,
  Paper,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Close as CloseIcon,
  Share as ShareIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Label as LabelIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ContentCopy as CopyIcon,
  Send as SendIcon,
  Note as NoteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  DragIndicator as DragIndicatorIcon,
  VerticalAlignTop as SmallIcon,
  VerticalAlignCenter as MediumIcon,
  Fullscreen as LargeIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from 'src/store';
import { updateContact, getContact, selectContactById } from 'src/features/account/contacts.slice';
import { toggleOpenConversation, fetchConversationsFullData, selectAllConversations } from 'src/features/account/conversations.slice';
import { useSnackbar } from 'notistack';
import { Formik, FieldArray } from 'formik';
import * as Yup from 'yup';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parseISO, isValid } from 'date-fns';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import ContactTimeline from './ContactTimeline';
import { httpClient } from 'src/libs';
import { Contact } from 'src/types/contacts';
import { isEqual } from 'lodash';
import ConversationCreateDialogV2 from '../Channels/ConversationCreateDialogV2';
import { setDrawerWidth } from 'src/features/user/user.slice';

// Validation schemas
const phoneSchema = Yup.object({
  value: Yup.string()
    .test('phone-format', 'Invalid phone format', function(value) {
      if (!value || value.trim() === '') return true; // Empty is okay
      return /^[\d\s\-\+\(\)]+$/.test(value);
    }),
  type: Yup.string().oneOf(['mobile', 'work', 'home', 'other']).nullable(),
  primary: Yup.boolean().nullable(),
});

const emailSchema = Yup.object({
  value: Yup.string()
    .test('email-format', 'Invalid email', function(value) {
      if (!value || value.trim() === '') return true; // Empty is okay
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }),
  type: Yup.string().oneOf(['personal', 'work', 'other']).nullable(),
  primary: Yup.boolean().nullable(),
});

const contactSchema = Yup.object({
  name: Yup.string().nullable(),
  first_name: Yup.string().nullable(),
  last_name: Yup.string().nullable(),
  email: Yup.string()
    .test('email-format', 'Invalid email', function(value) {
      if (!value || value.trim() === '') return true; // Empty is okay
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    })
    .nullable(),
  phone_number: Yup.string().nullable(),
  phones: Yup.array().of(phoneSchema).nullable().default([]),
  emails: Yup.array().of(emailSchema).nullable().default([]),
  tags: Yup.array().of(Yup.string()).nullable().default([]),
  shared_notes: Yup.string().nullable(),
  admin_notes: Yup.string().nullable(),
  company_name: Yup.string().nullable(),
  company_industry: Yup.string().nullable(),
  company_website: Yup.string().nullable(),
  status: Yup.string().nullable(),
  stage: Yup.string().nullable(),
  score: Yup.number().nullable(),
  deal_size: Yup.number().nullable(),
  assigned_to: Yup.string().nullable(),
  preferred_contact_method: Yup.string().nullable(),
  last_contacted: Yup.date().nullable(),
  timezone: Yup.string().nullable(),
  street_address_1: Yup.string().nullable(),
  street_address_2: Yup.string().nullable(),
  locality: Yup.string().nullable(),
  state: Yup.string().nullable(),
  zip: Yup.string().nullable(),
  country_code: Yup.string().nullable(),
});

// Props interface
interface ContactDrawerProps {
  contactId?: string;
  open?: boolean;
  onClose?: () => void;
  width?: number | string;
}


// Phone/Email item component
interface ContactMethodItemProps {
  item: any;
  type: 'phone' | 'email';
  onRemove: () => void;
  onChange: (field: string, value: any) => void;
  error?: any;
}

const ContactMethodItem: React.FC<ContactMethodItemProps> = ({ item, type, onRemove, onChange, error }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0.75,
        alignItems: 'center',
        mb: 0.75,
        p: 0.75,
        borderRadius: 1,
        bgcolor: theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.02)'
          : 'rgba(0, 0, 0, 0.01)',
        border: '1px solid',
        borderColor: theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(0, 0, 0, 0.05)',
        '&:hover': {
          bgcolor: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.04)'
            : 'rgba(0, 0, 0, 0.02)',
          borderColor: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      {type === 'phone' ?
        <PhoneIcon sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }} /> :
        <EmailIcon sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }} />
      }

      <TextField
        size="small"
        value={item.value}
        onChange={(e) => onChange('value', e.target.value)}
        placeholder={type === 'phone' ? 'Phone number' : 'Email address'}
        variant="outlined"
        fullWidth
        sx={{
          flex: 1,
          minWidth: 0,
          '& .MuiInputBase-root': {
            height: 36,
            fontSize: '0.875rem',
          },
          '& .MuiInputBase-input': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: error?.value ? theme.palette.error.main : theme.palette.divider,
          }
        }}
      />

      <FormControl size="small" sx={{ minWidth: 85, flexShrink: 0 }}>
        <Select
          value={item.type || (type === 'phone' ? 'mobile' : 'personal')}
          onChange={(e) => onChange('type', e.target.value)}
          sx={{
            height: 36,
            fontSize: '0.875rem',
            '& .MuiSelect-select': {
              py: 0.75,
            }
          }}
        >
          {type === 'phone' ? [
            <MenuItem key="mobile" value="mobile">Mobile</MenuItem>,
            <MenuItem key="work" value="work">Work</MenuItem>,
            <MenuItem key="home" value="home">Home</MenuItem>,
            <MenuItem key="other" value="other">Other</MenuItem>
          ] : [
            <MenuItem key="personal" value="personal">Personal</MenuItem>,
            <MenuItem key="work" value="work">Work</MenuItem>,
            <MenuItem key="other" value="other">Other</MenuItem>
          ]}
        </Select>
      </FormControl>

      <IconButton
        size="small"
        onClick={() => onChange('primary', !item.primary)}
        sx={{
          width: 32,
          height: 32,
          color: item.primary ? 'primary.main' : 'text.secondary',
          '&:hover': {
            bgcolor: 'action.hover',
          }
        }}
      >
        {item.primary ? <StarIcon sx={{ fontSize: 20 }} /> : <StarBorderIcon sx={{ fontSize: 20 }} />}
      </IconButton>

      <IconButton
        size="small"
        onClick={onRemove}
        sx={{
          width: 32,
          height: 32,
          color: 'text.secondary',
          '&:hover': {
            color: 'error.main',
            bgcolor: theme.palette.mode === 'dark' 
              ? 'rgba(244, 67, 54, 0.08)'
              : 'rgba(244, 67, 54, 0.04)',
          }
        }}
      >
        <DeleteIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Box>
  );
};

// WYSIWYG Editor component (simple for now)
const RichTextEditor: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: number;
}> = ({ value, onChange, placeholder, minHeight = 150 }) => {
  return (
    <TextField
      multiline
      fullWidth
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      minRows={6}
      sx={{
        '& .MuiInputBase-root': {
          minHeight,
        },
      }}
    />
  );
};

// Main ContactDrawer component
const ContactDrawer: React.FC<ContactDrawerProps> = ({
  contactId: propContactId,
  open: propOpen,
  onClose: propOnClose,
  width,
}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  // Optional width can be provided by the parent. If not provided, use
  // 100% on mobile and the persisted desktop width.
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const drawerWidth = useAppSelector(state => state.user.drawerWidth);
  const actualDrawerWidth = width ?? drawerWidth;

  // Determine contact ID and open state
  const urlContactId = searchParams.get('contact');
  const derivedContactId = propContactId ?? urlContactId ?? undefined;
  const open = propOpen !== undefined ? propOpen : !!urlContactId;

  // Measure actual drawer width so layout responds to expansion/contraction
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const initialMeasuredWidth = useMemo(() => {
    if (typeof actualDrawerWidth === 'number') return actualDrawerWidth;
    if (actualDrawerWidth === '100%') {
      return typeof window !== 'undefined' ? window.innerWidth : 0;
    }
    return 0;
  }, [actualDrawerWidth]);
  const [measuredWidth, setMeasuredWidth] = useState(initialMeasuredWidth);

  // Ensure we grab a real measurement as soon as the drawer is mounted
  useLayoutEffect(() => {
    if (drawerRef.current) {
      setMeasuredWidth(drawerRef.current.getBoundingClientRect().width);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    if (drawerRef.current) {
      setMeasuredWidth(drawerRef.current.offsetWidth);
    }
  }, [actualDrawerWidth, open]);

  // Track width changes precisely (e.g., CSS/layout changes, user resize)
  useEffect(() => {
    const element = drawerRef.current;
    if (!element) return;
    
    let rafId: number | null = null;
    
    const resizeObserver = new ResizeObserver((entries) => {
      // Use requestAnimationFrame to batch state updates and reduce loop pressure
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      
      rafId = requestAnimationFrame(() => {
        try {
          for (const entry of entries) {
            const nextWidth = entry.contentRect?.width ?? element.offsetWidth;
            setMeasuredWidth(nextWidth);
          }
        } catch (error) {
          // Silently handle any ResizeObserver errors
          // console.debug('ResizeObserver update handled gracefully:', error);
        }
        rafId = null;
      });
    });
    
    try {
      resizeObserver.observe(element);
    } catch (error) {
      // Handle any errors during observation setup
      console.warn('ResizeObserver setup error:', error);
    }
    
    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      try {
        resizeObserver.disconnect();
      } catch (error) {
        // Silently handle disconnect errors
      }
    };
  }, [open]);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = drawerRef.current?.offsetWidth || 0;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const newWidth = Math.min(
        Math.max(startWidth + (startX - moveEvent.clientX), 300),
        window.innerWidth
      );
      dispatch(setDrawerWidth(newWidth));
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [dispatch]);

  useEffect(() => {
    const handleResize = () => {
      if (drawerRef.current) {
        setMeasuredWidth(drawerRef.current.offsetWidth);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isCompact = isMobileScreen || measuredWidth < 600;

  // State
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  // State for external change detection
  const [hasExternalChanges, setHasExternalChanges] = useState(false);
  const [allowExternalUpdates, setAllowExternalUpdates] = useState(false);

  // State for conversation creation dialog
  const [conversationDialogOpen, setConversationDialogOpen] = useState(false);

  // Refs
  const initialContactRef = useRef<Contact | null>(null);

  // Preserve the contact ID for the lifetime of the open drawer so that
  // webhook-driven updates to the parent contact object can't clear it.
  // If a different contact is opened while the drawer is visible, switch to
  // the new ID; when the drawer closes, clear it entirely.
  const [contactId, setContactId] = useState<string | undefined>(derivedContactId);
  useEffect(() => {
    if (!open) {
      setContactId(undefined);
      return;
    }
    if (derivedContactId) {
      setContactId(derivedContactId);
    }
  }, [open, derivedContactId]);

  useEffect(() => {
    initialContactRef.current = null;
    setHasExternalChanges(false);
    setAllowExternalUpdates(false);
  }, [contactId]);


  // Get contact from Redux store
  const contact = useAppSelector(state => selectContactById(state, contactId));
  const isLoadingFromRedux = useAppSelector(state => state.contacts.loadingContactsIds.includes(contactId));

  // Get conversations from Redux store
  const allConversations = useAppSelector(selectAllConversations);
  const contactConversations = useMemo(() => {
    if (!contact?.conversations || contact.conversations.length === 0) return [];
    return allConversations.filter(conv => contact.conversations.includes(conv.id));
  }, [contact?.conversations, allConversations]);

  // Load contact data
  useEffect(() => {
    if (contactId && open && !contact) {
      setIsLoading(true);
      dispatch(getContact(contactId)).finally(() => {
        setIsLoading(false);
      });
    }
  }, [contactId, open, contact, dispatch]);

  // Fetch conversations when contact has conversation IDs but we don't have them in Redux
  useEffect(() => {
    if (contact?.conversations && contact.conversations.length > 0) {
      // Check which conversations we don't have in Redux yet
      const missingConversationIds = contact.conversations.filter(
        id => !allConversations.some(conv => conv.id === id)
      );

      if (missingConversationIds.length > 0) {
        dispatch(fetchConversationsFullData(missingConversationIds));
      }
    }
  }, [contact?.conversations, allConversations, dispatch]);

  // Initialize contact tracking
  useEffect(() => {
    if (open && contact) {
      initialContactRef.current = contact;
      setHasExternalChanges(false);
      setAllowExternalUpdates(false);
    }
  }, [open, contact?.id]);

  // Track external changes
  useEffect(() => {
    if (initialContactRef.current && contact && !allowExternalUpdates) {
      const hasChanges = !isEqual(initialContactRef.current, contact);
      setHasExternalChanges(hasChanges);
    }
  }, [contact, allowExternalUpdates]);

  // Generate share URL
  useEffect(() => {
    if (contactId && typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('contact', contactId);
      setShareUrl(url.toString());
    }
  }, [contactId]);

  // Handle drawer close
  const handleClose = useCallback(() => {
    // Reset state
    initialContactRef.current = null;
    setHasExternalChanges(false);
    setAllowExternalUpdates(false);

    if (propOnClose) {
      propOnClose();
    } else {
      searchParams.delete('contact');
      setSearchParams(searchParams);
    }
  }, [propOnClose, searchParams, setSearchParams]);

  // Handle accepting external changes
  const handleAcceptExternalChanges = useCallback(() => {
    setAllowExternalUpdates(true);
    setHasExternalChanges(false);
    initialContactRef.current = contact;
  }, [contact]);

  // Handle drawer width change with 3-position toggle
  const handleDrawerWidthChange = useCallback((event: React.MouseEvent<HTMLElement>, newWidth: string | null) => {
    if (newWidth !== null) {
      let width: number | string;
      switch (newWidth) {
        case 'small':
          width = 300;
          break;
        case 'medium':
          width = 600;
          break;
        case 'large':
          width = '100%';
          break;
        default:
          width = 600;
      }
      dispatch(setDrawerWidth(width));
    }
  }, [dispatch]);

  // Get current width position for toggle
  const getWidthPosition = useCallback(() => {
    if (actualDrawerWidth === 300) return 'small';
    if (actualDrawerWidth === '100%') return 'large';
    return 'medium';
  }, [actualDrawerWidth]);

  // Handle share
  const handleShare = useCallback(async () => {
    try {
      if (navigator.share && isMobileScreen) {
        await navigator.share({
          title: `Contact: ${contact?.name || 'Unknown'}`,
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        enqueueSnackbar('Share link copied to clipboard!', { variant: 'success' });
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  }, [shareUrl, contact, isMobileScreen, enqueueSnackbar]);

  // Handle form submission
  const handleSubmit = useCallback(async (values: any) => {
    if (!contactId || contact?.id !== contactId) {
      enqueueSnackbar('No contact selected', { variant: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      // Prepare data
      const updateData = {
        ...values,
        tags: Array.isArray(values.tags) ? values.tags.join(',') : values.tags,
        phones: values.phones.filter((p: any) => p.value), // Only send non-empty phones
        emails: values.emails.filter((e: any) => e.value), // Only send non-empty emails
      };

      // Call API
      await dispatch(updateContact({
        contactId,
        updates: updateData
      })).unwrap();

      enqueueSnackbar('Contact updated successfully', { variant: 'success' });
    } catch (error) {
      console.error('Failed to update contact:', error);
      enqueueSnackbar('Failed to update contact', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  }, [contact, contactId, dispatch, enqueueSnackbar]);

  // Initial form values
  const initialValues = useMemo(() => {
    if (!contact) {
      // Return empty default values when no contact
      return {
        name: '',
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        phones: [],
        emails: [],
        company_name: '',
        company_industry: '',
        company_website: '',
        tags: [],
        status: 'lead',
        stage: '',
        score: 0,
        deal_size: 0,
        assigned_to: '',
        shared_notes: '',
        admin_notes: '',
        preferred_contact_method: 'email',
        last_contacted: null,
        timezone: '',
        street_address_1: '',
        street_address_2: '',
        locality: '',
        state: '',
        zip: '',
        country_code: '',
      };
    }

    return {
      name: contact.name || '',
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      email: contact.email || '',
      phone_number: contact.phone_number || '',
      phones: contact.phones?.map(p => ({
        ...p,
        type: p.type || 'mobile'
      })) || (contact.phone_number ? [{
        value: contact.phone_number,
        type: 'mobile',
        primary: true
      }] : []),
      emails: contact.emails?.map(e => ({
        ...e,
        type: e.type || 'personal'
      })) || (contact.email ? [{
        value: contact.email,
        type: 'personal',
        primary: true
      }] : []),
      company_name: contact.company_name || '',
      company_industry: contact.company_industry || '',
      company_website: contact.company_website || '',
      tags: Array.isArray(contact.tags)
        ? contact.tags
        : (contact.tags as string | undefined)?.split(',').filter(Boolean) || [],
      status: contact.status || 'lead',
      stage: contact.stage || '',
      score: contact.score || 0,
      deal_size: contact.deal_size || 0,
      assigned_to: contact.assigned_to || '',
      shared_notes: contact.shared_notes || '',
      admin_notes: contact.admin_notes || '',
      preferred_contact_method: contact.preferred_contact_method || 'email',
      last_contacted: contact.last_contacted ? (typeof contact.last_contacted === 'string' ? parseISO(contact.last_contacted) : contact.last_contacted) : null,
      timezone: contact.timezone || '',
      street_address_1: contact.street_address_1 || '',
      street_address_2: contact.street_address_2 || '',
      locality: contact.locality || '',
      state: contact.state || '',
      zip: contact.zip || '',
      country_code: contact.country_code || '',
    };
  }, [contact]);

  // Don't render if no contactId
  if (!contactId || !open) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      variant="temporary"
      PaperProps={{
        ref: drawerRef,
        sx: {
          width: actualDrawerWidth,
          maxWidth: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Resize Handle and Width Toggle - Desktop Only */}
      {!isMobileScreen && (
        <>
          {/* Subtle resize handle on the left edge */}
          <Box
            onMouseDown={handleResizeStart}
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 4,
              cursor: 'col-resize',
              zIndex: 1,
              bgcolor: 'transparent',
              transition: 'background-color 0.2s',
              '&:hover': { 
                bgcolor: 'primary.main',
                opacity: 0.3,
              },
              '&:active': {
                bgcolor: 'primary.main',
                opacity: 0.5,
              }
            }}
          />

        </>
      )}

      {/* Header */}
      <Box>
        {hasExternalChanges && (
          <Box sx={{ p: 2, bgcolor: 'warning.main', color: 'warning.contrastText' }}>
            <Button
              variant="contained"
              color="inherit"
              size="small"
              onClick={handleAcceptExternalChanges}
              sx={{
                bgcolor: 'rgba(0,0,0,0.2)',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.3)',
                }
              }}
            >
              Contact was updated externally. Click to load latest changes.
            </Button>
          </Box>
        )}
        <Box
          sx={{
            p: 2,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, transparent 100%)'
              : 'linear-gradient(135deg, rgba(73, 73, 200, 0.05) 0%, transparent 100%)',
            borderBottom: '1px solid',
            borderColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(73, 73, 200, 0.1)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            position: 'relative',
          }}
        >
          <IconButton onClick={handleClose} edge="start">
            <CloseIcon />
          </IconButton>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '1.125rem',
                fontWeight: 600,
                lineHeight: 1.2
              }}
            >
              {isLoading || isLoadingFromRedux
                ? 'Loading...'
                : contact?.name ||
                (contact?.first_name || contact?.last_name
                  ? `${contact?.first_name || ''} ${contact?.last_name || ''}`.trim()
                  : 'Unknown Contact'
                )
              }
            </Typography>
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block'
              }}
            >
              {contact?.email || contact?.phone_number || 'No contact info'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Share contact">
              <IconButton onClick={handleShare} size="small">
                <ShareIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Width Toggle Control - Desktop Only */}
            {!isMobileScreen && (
              <Box
                sx={{
                  display: 'flex',
                  bgcolor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.03)',
                  borderRadius: 0.75,
                  p: 0.25,
                  gap: 0.25,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => handleDrawerWidthChange(null, 'small')}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 0.5,
                    bgcolor: getWidthPosition() === 'small' ? 'primary.main' : 'transparent',
                    color: getWidthPosition() === 'small' ? 'primary.contrastText' : 'text.secondary',
                    '&:hover': {
                      bgcolor: getWidthPosition() === 'small' ? 'primary.dark' : 'action.hover',
                    },
                  }}
                >
                  <Tooltip title="Narrow (300px)" placement="bottom">
                    <SmallIcon sx={{ fontSize: 16 }} />
                  </Tooltip>
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDrawerWidthChange(null, 'medium')}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 0.5,
                    bgcolor: getWidthPosition() === 'medium' ? 'primary.main' : 'transparent',
                    color: getWidthPosition() === 'medium' ? 'primary.contrastText' : 'text.secondary',
                    '&:hover': {
                      bgcolor: getWidthPosition() === 'medium' ? 'primary.dark' : 'action.hover',
                    },
                  }}
                >
                  <Tooltip title="Medium (600px)" placement="bottom">
                    <MediumIcon sx={{ fontSize: 16 }} />
                  </Tooltip>
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDrawerWidthChange(null, 'large')}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 0.5,
                    bgcolor: getWidthPosition() === 'large' ? 'primary.main' : 'transparent',
                    color: getWidthPosition() === 'large' ? 'primary.contrastText' : 'text.secondary',
                    '&:hover': {
                      bgcolor: getWidthPosition() === 'large' ? 'primary.dark' : 'action.hover',
                    },
                  }}
                >
                  <Tooltip title="Wide (100%)" placement="bottom">
                    <LargeIcon sx={{ fontSize: 16 }} />
                  </Tooltip>
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Tabs - Only show on compact view */}
      {isCompact && (
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          variant="fullWidth"
          sx={{
            borderBottom: '1px solid',
            borderColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(73, 73, 200, 0.1)',
            '& .MuiTab-root': {
              fontWeight: 500,
              fontSize: '0.875rem',
              letterSpacing: '0.01em',
              minHeight: 44,
              '&.Mui-selected': {
                fontWeight: 600,
                color: theme.palette.secondary.main,
              }
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '3px 3px 0 0',
              background: `linear-gradient(90deg, ${theme.palette.secondary.main} 0%, #5a5ad8 100%)`,
              boxShadow: `0 0 8px ${theme.palette.secondary.main}40`
            }
          }}
        >
          <Tab label="Details" />
          <Tab label="Activity" />
          <Tab label="Notes" />
        </Tabs>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {(isLoading || isLoadingFromRedux) ? (
          <Box sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Skeleton variant="text" height={40} />
              <Skeleton variant="rectangular" height={200} />
              <Skeleton variant="rectangular" height={100} />
            </Stack>
          </Box>
        ) : (
          <Formik
            key={contactId}
            initialValues={allowExternalUpdates ? initialValues : (initialContactRef.current ?
              {
                ...initialContactRef.current,
                tags: Array.isArray(initialContactRef.current.tags)
                  ? initialContactRef.current.tags
                  : (initialContactRef.current.tags as string | undefined)?.split(',').filter(Boolean) || [],
                phones: initialContactRef.current.phones?.map(p => ({
                  ...p,
                  type: p.type || 'mobile'
                })) || (initialContactRef.current.phone_number ? [{
                  value: initialContactRef.current.phone_number,
                  type: 'mobile',
                  primary: true
                }] : []),
                emails: initialContactRef.current.emails?.map(e => ({
                  ...e,
                  type: e.type || 'personal'
                })) || (initialContactRef.current.email ? [{
                  value: initialContactRef.current.email,
                  type: 'personal',
                  primary: true
                }] : []),
                last_contacted: initialContactRef.current.last_contacted ?
                  (typeof initialContactRef.current.last_contacted === 'string' ?
                    parseISO(initialContactRef.current.last_contacted) :
                    initialContactRef.current.last_contacted) :
                  null,
              } : initialValues)}
            validationSchema={contactSchema}
            onSubmit={handleSubmit}
            enableReinitialize={allowExternalUpdates}
          >
            {({ values, errors, handleChange, setFieldValue, handleSubmit: formikHandleSubmit }) => (
              <Box sx={{
                display: 'flex',
                flexDirection: isCompact ? 'column' : 'row',
                height: '100%',
                overflow: 'hidden'
              }}>
                {/* Left Column - Details Form */}
                <Box sx={{
                  flex: isCompact ? '1 1 auto' : '0 1 40%',
                  minWidth: isCompact ? '100%' : '320px',
                  maxWidth: isCompact ? '100%' : '45%',
                  overflow: 'auto',
                  display: (isCompact && tabValue === 1) ? 'none' : 'block'
                }}>
                  <form onSubmit={formikHandleSubmit}>
                    <Stack spacing={2} sx={{ p: 2 }}>
                      {/* Basic Info */}
                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                          <Avatar sx={{ width: 24, height: 24 }}>{contact?.first_name?.[0]}</Avatar>
                          Basic Information
                        </Typography>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                          <TextField
                            fullWidth
                            label="Full Name"
                            name="name"
                            value={values.name}
                            onChange={(e) => {
                              handleChange(e);
                              // Auto-populate from first name if name was empty and only first name is being typed
                              if (!values.name && !values.last_name && values.first_name) {
                                setFieldValue('name', e.target.value);
                              }
                            }}
                            size="small"
                            helperText="The display name for this contact"
                          />
                          <Stack direction={isCompact ? 'column' : 'row'} spacing={2}>
                            <TextField
                              fullWidth
                              label="First Name"
                              name="first_name"
                              value={values.first_name}
                              onChange={(e) => {
                                handleChange(e);
                                // Auto-populate name if it's empty
                                if (!values.name) {
                                  const newName = `${e.target.value} ${values.last_name || ''}`.trim();
                                  setFieldValue('name', newName);
                                }
                              }}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              label="Last Name"
                              name="last_name"
                              value={values.last_name}
                              onChange={(e) => {
                                handleChange(e);
                                // Auto-populate name if it's empty
                                if (!values.name) {
                                  const newName = `${values.first_name || ''} ${e.target.value}`.trim();
                                  setFieldValue('name', newName);
                                }
                              }}
                              size="small"
                            />
                          </Stack>
                        </Stack>
                      </Box>

                      <Divider />

                      {/* Phone Numbers - Condensed */}
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Typography variant="subtitle2" sx={{ 
                            fontWeight: 600, 
                            fontSize: '0.875rem',
                            color: theme.palette.text.primary,
                            letterSpacing: '0.01em'
                          }}>
                            Phone Numbers
                          </Typography>
                          <FieldArray name="phones">
                            {({ push }) => (
                              <IconButton
                                size="small"
                                onClick={() => push({ value: '', type: 'mobile', primary: false })}
                                sx={{ 
                                  width: 28,
                                  height: 28,
                                  bgcolor: theme.palette.mode === 'dark' 
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : 'rgba(0, 0, 0, 0.03)',
                                  '&:hover': {
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                  }
                                }}
                              >
                                <AddIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            )}
                          </FieldArray>
                        </Box>
                        <FieldArray name="phones">
                          {({ remove }) => (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {values.phones?.map((phone: any, index: number) => (
                                <ContactMethodItem
                                  key={index}
                                  item={phone}
                                  type="phone"
                                  onRemove={() => remove(index)}
                                  onChange={(field, value) => setFieldValue(`phones.${index}.${field}`, value)}
                                  error={errors.phones?.[index]}
                                />
                              ))}
                            </Box>
                          )}
                        </FieldArray>
                      </Box>

                      {/* Email Addresses - Condensed */}
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Typography variant="subtitle2" sx={{ 
                            fontWeight: 600, 
                            fontSize: '0.875rem',
                            color: theme.palette.text.primary,
                            letterSpacing: '0.01em'
                          }}>
                            Email Addresses
                          </Typography>
                          <FieldArray name="emails">
                            {({ push }) => (
                              <IconButton
                                size="small"
                                onClick={() => push({ value: '', type: 'personal', primary: false })}
                                sx={{ 
                                  width: 28,
                                  height: 28,
                                  bgcolor: theme.palette.mode === 'dark' 
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : 'rgba(0, 0, 0, 0.03)',
                                  '&:hover': {
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                  }
                                }}
                              >
                                <AddIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            )}
                          </FieldArray>
                        </Box>
                        <FieldArray name="emails">
                          {({ remove }) => (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {values.emails?.map((email: any, index: number) => (
                                <ContactMethodItem
                                  key={index}
                                  item={email}
                                  type="email"
                                  onRemove={() => remove(index)}
                                  onChange={(field, value) => setFieldValue(`emails.${index}.${field}`, value)}
                                  error={errors.emails?.[index]}
                                />
                              ))}
                            </Box>
                          )}
                        </FieldArray>
                      </Box>

                      <Divider />

                      {/* Company Info */}
                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                          <BusinessIcon fontSize="small" />
                          Company Information
                        </Typography>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                          <TextField
                            fullWidth
                            label="Company Name"
                            name="company_name"
                            value={values.company_name}
                            onChange={handleChange}
                            size="small"
                          />
                          <Stack direction={isCompact ? 'column' : 'row'} spacing={2}>
                            <TextField
                              fullWidth
                              label="Industry"
                              name="company_industry"
                              value={values.company_industry}
                              onChange={handleChange}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              label="Website"
                              name="company_website"
                              value={values.company_website}
                              onChange={handleChange}
                              size="small"
                            />
                          </Stack>
                        </Stack>
                      </Box>

                      <Divider />

                      {/* Tags */}
                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                          <LabelIcon fontSize="small" />
                          Tags
                        </Typography>
                        <Box
                          sx={{
                            mt: 2,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            p: 1.5,
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            minHeight: 48,
                            alignItems: 'center',
                            bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'background.default',
                            cursor: 'text',
                            '&:hover': {
                              borderColor: 'text.secondary',
                            },
                            '&:focus-within': {
                              borderColor: 'primary.main',
                              borderWidth: 2,
                              p: 'calc(12px - 1px)',
                            }
                          }}
                          onClick={() => {
                            const input = document.getElementById('tag-input');
                            if (input) input.focus();
                          }}
                        >
                          {values.tags?.map((tag: string, index: number) => (
                            <Chip
                              key={index}
                              label={tag}
                              size="small"
                              onDelete={() => {
                                const newTags = [...values.tags];
                                newTags.splice(index, 1);
                                setFieldValue('tags', newTags);
                              }}
                              sx={{
                                height: 28,
                                '& .MuiChip-label': {
                                  px: 1.5,
                                  fontSize: '0.875rem',
                                }
                              }}
                            />
                          ))}
                          <input
                            id="tag-input"
                            type="text"
                            placeholder={values.tags?.length === 0 ? "Add tags..." : ""}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ',') {
                                e.preventDefault();
                                const input = e.target as HTMLInputElement;
                                const value = input.value.trim();
                                if (value && !values.tags?.includes(value)) {
                                  setFieldValue('tags', [...(values.tags || []), value]);
                                  input.value = '';
                                }
                              }
                            }}
                            onBlur={(e) => {
                              const value = e.target.value.trim();
                              if (value && !values.tags?.includes(value)) {
                                setFieldValue('tags', [...(values.tags || []), value]);
                                e.target.value = '';
                              }
                            }}
                            style={{
                              border: 'none',
                              outline: 'none',
                              backgroundColor: 'transparent',
                              fontSize: '0.875rem',
                              padding: '6px 8px',
                              minWidth: 120,
                              flexGrow: 1,
                              fontFamily: 'inherit',
                              color: theme.palette.text.primary,
                              lineHeight: '16px',
                              height: '28px',
                            }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          Press Enter or comma to add a tag
                        </Typography>
                      </Box>

                      {/* Status & Scoring */}
                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                          Status & Scoring
                        </Typography>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                          <Stack direction={isCompact ? 'column' : 'row'} spacing={2}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Status</InputLabel>
                              <Select
                                name="status"
                                value={values.status || 'lead'}
                                onChange={handleChange}
                                label="Status"
                              >
                                <MenuItem value="lead">Lead</MenuItem>
                                <MenuItem value="prospect">Prospect</MenuItem>
                                <MenuItem value="customer">Customer</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                              </Select>
                            </FormControl>
                            <TextField
                              fullWidth
                              label="Stage"
                              name="stage"
                              value={values.stage}
                              onChange={handleChange}
                              size="small"
                            />
                          </Stack>
                          <Stack direction={isCompact ? 'column' : 'row'} spacing={2}>
                            <TextField
                              fullWidth
                              label="Score"
                              name="score"
                              type="number"
                              value={values.score}
                              onChange={handleChange}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              label="Deal Size"
                              name="deal_size"
                              type="number"
                              value={values.deal_size}
                              onChange={handleChange}
                              size="small"
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                              }}
                            />
                          </Stack>
                        </Stack>
                      </Box>

                      <Divider />

                      {/* Communication Preferences */}
                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                          Communication Preferences
                        </Typography>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Preferred Contact Method</InputLabel>
                            <Select
                              name="preferred_contact_method"
                              value={values.preferred_contact_method || 'email'}
                              onChange={handleChange}
                              label="Preferred Contact Method"
                            >
                              <MenuItem value="email">Email</MenuItem>
                              <MenuItem value="phone">Phone</MenuItem>
                              <MenuItem value="sms">SMS</MenuItem>
                              <MenuItem value="do_not_contact">Do Not Contact</MenuItem>
                            </Select>
                          </FormControl>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DateTimePicker
                              label="Last Contacted"
                              value={values.last_contacted}
                              onChange={(date) => setFieldValue('last_contacted', date)}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: 'small',
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </Stack>
                      </Box>

                      <Divider />

                      {/* Address */}
                      <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                          <LocationIcon fontSize="small" />
                          Address
                        </Typography>
                        <Stack spacing={2} sx={{ mt: 2 }}>
                          <TextField
                            fullWidth
                            label="Street Address 1"
                            name="street_address_1"
                            value={values.street_address_1}
                            onChange={handleChange}
                            size="small"
                          />
                          <TextField
                            fullWidth
                            label="Street Address 2"
                            name="street_address_2"
                            value={values.street_address_2}
                            onChange={handleChange}
                            size="small"
                          />
                          <Stack direction={isCompact ? 'column' : 'row'} spacing={2}>
                            <TextField
                              fullWidth
                              label="City"
                              name="locality"
                              value={values.locality}
                              onChange={handleChange}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              label="State"
                              name="state"
                              value={values.state}
                              onChange={handleChange}
                              size="small"
                            />
                            <TextField
                              fullWidth
                              label="ZIP"
                              name="zip"
                              value={values.zip}
                              onChange={handleChange}
                              size="small"
                            />
                          </Stack>
                        </Stack>
                      </Box>

                      {/* Notes Section - Show on desktop or when tabValue is 2 on mobile */}
                      {(!isCompact || tabValue === 2) && (
                        <>
                          <Divider />
                          <Box>
                            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                              <NoteIcon fontSize="small" />
                              Notes
                            </Typography>
                            <Stack spacing={2} sx={{ mt: 2 }}>
                              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Shared Notes
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                  These notes will be visible to all team members
                                </Typography>
                                <RichTextEditor
                                  value={values.shared_notes}
                                  onChange={(value) => setFieldValue('shared_notes', value)}
                                  placeholder="Add shared notes..."
                                  minHeight={150}
                                />
                              </Paper>

                              <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Admin Notes
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                  These notes are only visible to administrators
                                </Typography>
                                <RichTextEditor
                                  value={values.admin_notes}
                                  onChange={(value) => setFieldValue('admin_notes', value)}
                                  placeholder="Add admin notes..."
                                  minHeight={150}
                                />
                              </Paper>
                            </Stack>
                          </Box>
                        </>
                      )}

                      {/* Submit Button - Show on details tab (0) or notes tab (2) */}
                      {(tabValue === 0 || tabValue === 2) && (
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          disabled={isSubmitting || !contact?.id}
                          startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
                          sx={{
                            mt: 2,
                            transition: 'all 0.3s',
                            '&.Mui-disabled': {
                              opacity: 0.7,
                            }
                          }}
                        >
                          {isSubmitting ? (tabValue === 2 ? 'Saving Notes...' : 'Saving...') : (tabValue === 2 ? 'Save Notes' : 'Save Changes')}
                        </Button>
                      )}
                    </Stack>
                  </form>
                </Box>

                {/* Divider between columns on desktop */}
                {!isCompact && (
                  <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                )}

                {/* Right Column - Timeline (Desktop) or Activity Tab (Mobile) */}
                <Box sx={{
                  flex: '1 1 auto',
                  minWidth: 0, // Allow shrinking
                  overflow: 'auto',
                  display: (isCompact && tabValue !== 1) ? 'none' : 'block',
                  p: 2
                }}>
                  <ContactTimeline
                    contactId={contactId!}
                    conversations={contactConversations}
                    onConversationClick={(conversationId, channelId) => {
                      // Open the conversation using the same mechanism as ConversationRowItem
                      dispatch(toggleOpenConversation(conversationId));
                    }}
                    onNewMessage={() => {
                      setConversationDialogOpen(true);
                    }}
                    onAddNote={() => setTabValue(2)}
                  />
                </Box>
              </Box>
            )}
          </Formik>
        )}
      </Box>

      {/* Conversation Creation Dialog */}
      {contactId && (
        <ConversationCreateDialogV2
          open={conversationDialogOpen}
          handleClose={() => setConversationDialogOpen(false)}
          incomingContactId={contactId}
          incomingDraftMessage=""
          incomingChannelId=""
        />
      )}
    </Drawer>
  );
};

// Create context for drawer state
interface ContactDrawerContextType {
  contactId: string | null;
  isOpen: boolean;
  openDrawer: (contactId: string) => void;
  closeDrawer: () => void;
}

const ContactDrawerContext = createContext<ContactDrawerContextType | null>(null);

// Context Provider that manages ContactDrawer state globally
const ContactDrawerProvider: React.FC<{ children: React.ReactNode; width?: number | string }> = React.memo(({ children, width }) => {
  const [searchParams] = useSearchParams();
  const [localContactId, setLocalContactId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Read from URL on mount only
  const urlContactId = searchParams.get('contact');
  useEffect(() => {
    if (urlContactId) {
      setLocalContactId(urlContactId);
      setIsOpen(true);
    }
  }, []); // Empty deps - only run on mount

  // Set mobile width on mount if needed
  useEffect(() => {
    if (isMobile) {
      const currentWidth = localStorage.getItem('oppy_drawer_width');
      if (currentWidth !== '100%') {
        dispatch(setDrawerWidth('100%'));
      }
    }
  }, [isMobile, dispatch]);

  const openDrawer = useCallback((contactId: string) => {
    // Just update local state - no URL changes
    setLocalContactId(contactId);
    setIsOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    // Just update local state - no URL changes
    setIsOpen(false);
    setLocalContactId(null);
  }, []);

  const contextValue = useMemo(() => ({
    contactId: localContactId,
    isOpen,
    openDrawer,
    closeDrawer
  }), [localContactId, isOpen, openDrawer, closeDrawer]);

  return (
    <ContactDrawerContext.Provider value={contextValue}>
      {children}
      {localContactId && isOpen && (
        <ContactDrawer
          contactId={localContactId}
          open={true}
          onClose={closeDrawer}
          width={width}
        />
      )}
    </ContactDrawerContext.Provider>
  );
});

ContactDrawerProvider.displayName = 'ContactDrawerProvider';

// Export with global state provider for easy access
export default ContactDrawerProvider;

// Also export the drawer component directly for manual usage
export { ContactDrawer };

// Hook for easy drawer control from any component
export const useContactDrawer = () => {
  const context = useContext(ContactDrawerContext);

  if (!context) {
    throw new Error('useContactDrawer must be used within ContactDrawerProvider');
  }

  return context;
};
